import { TWidget } from 'widgets/Widget';
import { getScrollPosition, onScroll } from 'widgets/toolbox/scroll';

/**
 * @description Base CartStickyPanel implementation
 * @param Widget Base widget for extending
 * @returns CartStickyPanel class
 */
function CartStickyPanelClassCreator(Widget: TWidget) {
    /**
     * @category widgets
     * @subcategory cart
     * @class CartStickyPanel
     * @augments Widget
     * @property {string} data-widget - Widget name `CartStickyPanel`
     */

    class CartStickyPanel extends Widget {
        stickyPanelOffset: number = 0;

        private contentObserver?: MutationObserver;

        prefs() {
            return {
                proceedToCheckoutBtnClass: '.b-summary_section.m-payments .b-cart_actions-button',
                stickyClass: 'm-sticky',
                isAlwaysSticky: false,
                ...super.prefs()
            };
        }

        init() {
            super.init();
            this.initStickyPanelOffset();
            this.handleScroll();
            this.observeContentChanges();
            this.eventBus().on('cart.updated', 'onCartUpdated');
        }

        /**
         * @description Recalculates the sticky panel offset and re-evaluates the sticky state
         * after the cart has been updated (e.g. a product was removed).
         */
        onCartUpdated() {
            this.initStickyPanelOffset();
            this.handleScroll();
        }

        /**
         * @description Observes another element on the page for DOM mutations
         * (content inserted, removed or moved) and recalculates the sticky state.
         */
        private observeContentChanges() {
            // change the id/selector to the element you want to watch
            const target = document.getElementById('maincontent');

            if (!(target instanceof HTMLElement)) {
                return;
            }

            this.contentObserver = new MutationObserver(mutations => {
                const hasContentChange = mutations.some(m => m.type === 'childList' && (m.addedNodes.length > 0 || m.removedNodes.length > 0));

                if (hasContentChange) {
                    this.onCartUpdated();
                }
            });

            this.contentObserver.observe(target, {
                childList: true, // detect inserted/removed children
                subtree: true // also watch nested elements
            });
        }

        private initStickyPanelOffset() {
            const stickyPanel = document.getElementById('checkoutButtons');

            this.stickyPanelOffset = (stickyPanel?.getBoundingClientRect().top ?? 0) + window.scrollY;
        }

        private recalcOffset(stickyPanel: HTMLElement, footer: HTMLElement | null) {
            return {
                stickyPanelOffset: stickyPanel.getBoundingClientRect().top + window.scrollY,
                footerOffset: footer?.offsetTop ?? 0
            };
        }

        /**
         * Toggles sticky class
         *
         * @param isSticky - Whether component is sticky
         */
        private toggleSticky(isSticky: boolean) {
            if (isSticky) {
                this.ref('self').addClass(this.prefs().stickyClass);
            } else {
                this.ref('self').removeClass(this.prefs().stickyClass);
            }
        }

        /**
         * Checks whether the sticky panel is currently within the viewport.
         *
         * @param stickyPanel - The sticky panel element
         * @returns boolean indicating if the panel is visible in the viewport
         */
        private isPanelInViewport(stickyPanel: HTMLElement): boolean {
            const rect = stickyPanel.getBoundingClientRect();

            return rect.top < window.innerHeight && rect.bottom > 0;
        }

        handleScroll() {
            const stickyPanel = document.getElementById('checkoutButtons');
            const footer = document.getElementById('footercontent');

            if (!(stickyPanel instanceof HTMLElement)) {
                return;
            }

            let offsets = this.recalcOffset(stickyPanel, footer);
            let isSticky = this.prefs().isAlwaysSticky && !this.isPanelInViewport(stickyPanel);

            if (isSticky) {
                this.toggleSticky(true);
            }

            window.addEventListener('resize', () => {
                offsets = this.recalcOffset(stickyPanel, footer);

                if (this.prefs().isAlwaysSticky && !this.isPanelInViewport(stickyPanel) && window.scrollY < offsets.footerOffset) {
                    this.toggleSticky(true);
                }
            });

            onScroll(() => {
                const scrollPosition = getScrollPosition();
                const currentFooterOffset = footer?.offsetTop ?? 0;

                if (Math.abs(currentFooterOffset - offsets.footerOffset) > 50) {
                    offsets = this.recalcOffset(stickyPanel, footer);
                }

                const isAboveStickyPanel = scrollPosition < this.stickyPanelOffset - window.innerHeight && this.prefs().isAlwaysSticky;
                const isBelowStickyPanel = scrollPosition > this.stickyPanelOffset && scrollPosition < offsets.footerOffset;
                const shouldBeSticky = isAboveStickyPanel || isBelowStickyPanel;

                if (shouldBeSticky !== isSticky) {
                    this.toggleSticky(shouldBeSticky);
                    isSticky = shouldBeSticky;
                }
            });
        }

        proceedToCheckout() {
            const proceedToCheckoutBtnClass = this.prefs().proceedToCheckoutBtnClass;
            const proceedButton = document.querySelector<HTMLSpanElement>(`${proceedToCheckoutBtnClass}`);

            if (!proceedButton) {
                return;
            }

            proceedButton.click();
        }

        /**
         * @description Cleanup on widget destroy to avoid memory leaks.
         */
        destroy() {
            this.contentObserver?.disconnect();
            super.destroy();
        }
    }

    return CartStickyPanel;
}

export type TCartStickyPanel = ReturnType<typeof CartStickyPanelClassCreator>;

export type TCartStickyPanelInstance = InstanceType<TCartStickyPanel>;

export default CartStickyPanelClassCreator;
